<template>
  <div class="c-hreflang">
    <div class="c-page-header c-page-header--flush">
      <div>
        <h3 class="c-page-header__title">
          Cross-domain hreflang
        </h3>
        <p class="c-page-header__subtitle">
          Choose which websites are linked with hreflang tags, in what order, which pages they apply to, and which one is x-default.
        </p>
      </div>
      <div class="c-page-header__actions">
        <span class="c-hreflang__summary">
          <strong>{{ getLinkedCount() }}</strong> linked
          <template v-if="xDefaultName">
            · x-default: <strong>{{ xDefaultName }}</strong>
          </template>
        </span>
        <button
          type="button"
          class="ui-btn ui-btn--primary"
          :disabled="!canEdit || !rows.length || hasLinkedWithoutPages()"
          :title="hasLinkedWithoutPages() ? 'Every linked website needs at least one included page' : null"
          @click="updateSeoLinkedWebsites"
        >
          <app-icon name="save" />
          Save changes
        </button>
      </div>
    </div>

    <div
      v-if="error && error.messages"
      class="ui-alert ui-alert--danger c-hreflang__alert"
      role="alert"
    >
      <app-icon name="error" />
      <span>{{ error.messages }}</span>
    </div>

    <ol
      v-if="rows.length"
      class="c-hreflang__list"
    >
      <li
        v-for="(row, index) in rows"
        :key="row.rowData.id"
        :class="['c-hreflang-card', { 'is-linked': row.selected, 'is-locked': row.disabled }]"
      >
        <div class="c-hreflang-card__main">
          <!-- order -->
          <div class="c-hreflang-card__order">
            <button
              type="button"
              class="c-hreflang-card__move"
              title="Move up (Shift: to top)"
              aria-label="Move up"
              :disabled="!canMove(row, index, 'up')"
              @click="moveUpWebsite(row, $event.shiftKey)"
            >
              <app-icon
                name="keyboard_arrow_up"
                :size="18"
              />
            </button>
            <span class="c-hreflang-card__position">{{ index + 1 }}</span>
            <button
              type="button"
              class="c-hreflang-card__move"
              title="Move down (Shift: to bottom)"
              aria-label="Move down"
              :disabled="!canMove(row, index, 'down')"
              @click="moveDownWebsite(row, $event.shiftKey)"
            >
              <app-icon
                name="keyboard_arrow_down"
                :size="18"
              />
            </button>
          </div>

          <!-- linked switch -->
          <md-switch
            v-model="row.selected"
            class="c-hreflang-card__switch"
            :disabled="row.disabled"
            :aria-label="`Link ${row.rowData.name}`"
            @change="onCheckboxChange(row)"
          />

          <!-- website -->
          <div class="c-hreflang-card__site">
            <div class="c-hreflang-card__name">
              {{ row.rowData.name }}
              <span
                v-if="row.rowData.id === currentWebsiteId"
                class="c-hreflang-card__tag"
              >This website</span>
              <span
                v-if="row.disabled"
                class="c-hreflang-card__tag c-hreflang-card__tag--locked"
              >
                <app-icon
                  name="lock"
                  :size="12"
                />
                No access
              </span>
            </div>
            <div class="c-hreflang-card__url">
              {{ row.rowData.url }}
            </div>
          </div>

          <span class="c-hreflang-card__country">{{ row.rowData.countryIsoCode }}</span>

          <!-- x-default -->
          <button
            type="button"
            :class="['c-hreflang-card__xdefault', { 'is-active': row.xDefault }]"
            :disabled="!row.selected || row.disabled"
            :aria-pressed="String(!!row.xDefault)"
            :title="row.xDefault ? 'Remove x-default' : 'Use as x-default (fallback for unmatched languages)'"
            @click="onCheckboxXDefaultChange(row)"
          >
            <app-icon
              :name="row.xDefault ? 'star' : 'star_outline'"
              :size="16"
              :filled="row.xDefault"
            />
            x-default
          </button>
        </div>

        <!-- included pages -->
        <div
          v-if="row.selected"
          class="c-hreflang-card__pages"
        >
          <span class="c-hreflang-card__pages-label">Included pages</span>
          <div class="ui-chip-group">
            <button
              v-for="page in includePagesOptions"
              :key="`${row.rowData.id}-${page.id}`"
              type="button"
              :class="['ui-chip', { 'ui-chip--active': row.includePages && row.includePages[page.name] }]"
              :aria-pressed="String(!!(row.includePages && row.includePages[page.name]))"
              :disabled="row.disabled"
              @click="toggleIncludePage(row, page.name)"
            >
              <app-icon
                v-if="row.includePages && row.includePages[page.name]"
                name="check"
                :size="14"
              />
              {{ page.name }}
            </button>
          </div>
          <span
            v-if="!hasIncludedPage(row)"
            class="c-hreflang-card__warning"
          >
            <app-icon
              name="warning"
              :size="14"
            />
            Select at least one page
          </span>
        </div>
      </li>
    </ol>

    <div
      v-else
      class="c-table__empty"
    >
      <span class="c-table__empty-icon">
        <app-icon
          name="language"
          :size="24"
        />
      </span>
      <p class="c-table__empty-title">
        No websites available
      </p>
      <p>There are no websites to link yet.</p>
    </div>
  </div>
</template>
  
<script>
  
import { PERMISSION_NAMES } from "@/const/online-permissions";
import AppIcon from '@/shared/app-icon/AppIcon'
import { websiteService } from '@/services/websites'
import { seoLinkedWebesitesService } from '@/services/seo-linked-websites'
import { onlinePermissionsMixin } from '@/mixins/onlinePermissionsMixin'
import { mapActions, mapGetters } from 'vuex'
import {
  sortWebsitesByPosition,
  buildSelectedPayload,
  buildRemovedPayload,
  moveWebsiteInList,
  getErrorMessage,
  prepareSeoLinkedWebsiteDataToReorder,
} from '@/helpers/seoLinkedWebsitesHelper';
import { belgiumCasinoWebsiteId, belgiumSportsWebsiteId } from "../../../../../const/seo-rules";

export default {
  name: "SeoLinkedWebsites",
  components: {
    AppIcon,
  },

  mixins: [onlinePermissionsMixin],

  props: {
    website: {
    type: Object,
    default: null,
    },
  },

  data() {
    return {
      websites: [],
      seoLinkedWebsites: [],
      currentWebsiteId: null,
      error: null,
      selectedIds: new Set(),
      selectedXDefaultId: null,
      // reactive copy of tableData (the old BaseTableComponent did the same via its own data);
      // the row objects must be reactive so switches / x-default toggles re-render
      rows: [],
    }
  },

  computed: {
    PERMISSION_NAMES() {
      return PERMISSION_NAMES
    },

    canEdit() {
      return this.canWrite(`${PERMISSION_NAMES.WEB_SITES.key}.${PERMISSION_NAMES.SEO_RULES.key}`);
    },

    xDefaultName() {
      const row = this.rows.find(item => item.rowData.id === this.selectedXDefaultId);

      return row ? row.rowData.name : '';
    },

    includePagesOptions() {
      return this.customPages || [];
    },

    positionMap() {
      return new Map(
        this.seoLinkedWebsites.map(item => [
          item.rowData.websiteTargetId,
          item.rowData.position,
          item.rowData.xDefault
        ])
      );
    },

    formattedSeoLinkedWebsites() {
      if (!this.websites.length) {
        return [];
      }

      return this.websites.map(website => ({
        ...website,
        selected: this.selectedIds.has(website.rowData.id),
        xDefault: this.selectedXDefaultId === website.rowData.id
      }));
    },

    tableData() {
      if (!this.currentWebsiteId) {
        return [];
      }
      
      return this.formattedSeoLinkedWebsites;
    },

    isUpdateDisabled() {
      return this.tableData
        .filter(row => row.selected)
        .some(row => !this.includePagesOptions.some(page => Boolean(
          row.includePages && row.includePages[page.name]
        )));
    },

    ...mapGetters('customPages', ['customPages']),
  },

  watch: {
    tableData: {
      immediate: true,
      handler(value) {
        this.rows = value;
      },
    },
  },

  async created() {
    this.currentWebsiteId = this.website?.id ?? Number(this.$route.params.id);
    await Promise.all([
      this.getWebsites(),
      this.getSeoLinkedWebsites(),
      this.getCustomPages(true)
    ]);
    this.setInitialIncludePagesStates();
  },

  methods: {
    ...mapActions('response', ['handleSuccess']),
    ...mapActions('customPages', ['getCustomPages']),

    async updateSeoLinkedWebsites() {
      this.error = null;

      try {
        const selectedData = this.tableData;

        const selected = buildSelectedPayload(selectedData, this.currentWebsiteId, this.selectedIds);
        const payload = {
          selected,
          removed: buildRemovedPayload(selected, this.seoLinkedWebsites),
          websiteId: this.$route.params.id,
        };

        const updatedData = await seoLinkedWebesitesService.updateSeoLinkedWebsites(payload);
        this.seoLinkedWebsites = prepareSeoLinkedWebsiteDataToReorder(updatedData);
        this.sortWebsitesByInitialPositions();
        this.handleSuccess({ message: 'SEO linked websites updated!' });
      } catch (error) {
        this.error = { 
          messages: getErrorMessage(error)
        };
      }
    },

    async getWebsites(page = 1, limit = 1) {
      const response = await websiteService.getWebsites(page, limit);
      let websites = websiteService
        .prepareWebsitesDataForSeoLinkedWebsites(response.data)
        .map(preparedWebsiteData => ({
          ...preparedWebsiteData,
          disabled: !this.canReadWebsite(preparedWebsiteData.rowData.id)
        }));

      if (this.currentWebsiteId === belgiumSportsWebsiteId) {
        websites = websites.filter(w => w.rowData.id !== belgiumCasinoWebsiteId);
      } else if (this.currentWebsiteId === belgiumCasinoWebsiteId) {
        websites = websites.filter(w => w.rowData.id !== belgiumSportsWebsiteId);
      }

      this.websites = websites;
    },

    async getSeoLinkedWebsites() {
      const response = await seoLinkedWebesitesService.getSeoLinkedWebsites(this.$route.params.id);
      this.seoLinkedWebsites = seoLinkedWebesitesService.prepareSeoLinkedWebsitesDataFromResponse(response.data);
      this.selectedIds = new Set(
        this.seoLinkedWebsites.map(item => item.rowData.websiteTargetId)
      );
      this.sortWebsitesByInitialPositions();

      this.websites = this.websites.map(website => {
        const xDefaultValue = this.seoLinkedWebsites.findIndex(item => item.rowData.websiteTargetId === website.rowData.id && item.rowData.xDefault) !== -1;

        if (xDefaultValue) {
           this.selectedXDefaultId = website.rowData.id;
        }
        return {
        ...website,
        xDefault: xDefaultValue
      }
      });
    },

    setInitialIncludePagesStates() {
      const linkedWebsiteMap = new Map(
        this.seoLinkedWebsites.map(item => [
          item.rowData.websiteTargetId,
          item
        ])
      );

      this.websites = this.websites.map(website => {
        const linkedWebsite = linkedWebsiteMap.get(website.rowData.id);
        const savedIncludePages = linkedWebsite?.rowData?.includePages ?? linkedWebsite?.rowData?.includedPages ?? [];

        return {
          ...website,
          includePages: this.createIncludePagesState(savedIncludePages)
        };
      });
    },

    createIncludePagesState(savedPages = []) {
      const selectedPageNames = new Set(
        savedPages.map(page => typeof page === 'string' ? page : page.name).filter(Boolean)
      );

      return Object.fromEntries(
        this.includePagesOptions.map(page => [page.name, selectedPageNames.has(page.name)])
      );
    },

    isIncludePagesRowVisible(row) {
      return row.selected;
    },

    onIncludePageChange(row, pageName) {
      const website = this.websites.find(
        item => item.rowData.id === row.rowData.id
      );

      if (!website) {
        return;
      }

      this.$set(website.includePages, pageName, Boolean(row.includePages[pageName]));
    },

    // Rows are rebuilt in a computed and `selectedIds` is a Set (not reactive in Vue 2),
    // so these are evaluated on every render instead of being cached as computeds.
    getLinkedCount() {
      return this.rows.filter(row => row.selected).length;
    },

    hasLinkedWithoutPages() {
      return this.rows.some(row => row.selected && !this.hasIncludedPage(row));
    },

    // mirrors moveWebsiteInList: the current website is pinned first and nothing moves above it
    canMove(row, index, direction) {
      if (row.disabled || row.rowData.id === this.currentWebsiteId) {
        return false;
      }

      const pinnedFirst = this.rows.length && this.rows[0].rowData.id === this.currentWebsiteId;
      const firstMovableIndex = pinnedFirst ? 1 : 0;

      return direction === 'up' ? index > firstMovableIndex : index < this.rows.length - 1;
    },

    hasIncludedPage(row) {
      return this.includePagesOptions.some(page => Boolean(row.includePages && row.includePages[page.name]));
    },

    toggleIncludePage(row, pageName) {
      this.$set(row.includePages, pageName, !row.includePages[pageName]);
      this.onIncludePageChange(row, pageName);
    },

    clearIncludePages(row) {
      this.includePagesOptions.forEach(page => {
        this.$set(row.includePages, page.name, false);
      });
    },

    moveWebsite(row, direction, shiftKeyPressed) {
      if (row.disabled) {
        return;
      }

      this.websites = moveWebsiteInList(
        this.websites,
        row.rowData.id,
        this.currentWebsiteId,
        direction,
        shiftKeyPressed
      );
    },

    moveUpWebsite(row, shiftKeyPressed) {
      this.moveWebsite(row, 'up', shiftKeyPressed);
    },

    moveDownWebsite(row, shiftKeyPressed) {
      this.moveWebsite(row, 'down', shiftKeyPressed);
    },

    sortWebsitesByInitialPositions() {
      if (!this.websites.length || !this.seoLinkedWebsites.length) {
        return;
      }
      this.websites = sortWebsitesByPosition(this.websites, this.positionMap);
    },

    onCheckboxXDefaultChange(row) {
      if (this.selectedXDefaultId === row.rowData.id) {
        row.xDefault = false;
        this.selectedXDefaultId = null;
        return;
      }

       if (this.selectedXDefaultId && (this.selectedXDefaultId !== row.rowData.id)) {
        row.xDefault = false;
        return;
      }

      this.selectedXDefaultId = row.rowData.id;
      row.xDefault = true;  
    },

    onCheckboxChange(row) {
      if (row.disabled) {
        row.selected = false;
        row.xDefault = false;
        return;
      }

      if (row.selected) {
        this.selectedIds.add(row.rowData.id);
      } else {
        this.selectedIds.delete(row.rowData.id);
        this.clearIncludePages(row);
        if (this.selectedXDefaultId === row.rowData.id) {
          row.xDefault = false;
          this.selectedXDefaultId = null;
        }
      }
    },
  }
}
</script>
  
<style lang="scss" scoped>
.c-hreflang {
  display: flex;
  flex-direction: column;
}

.c-hreflang__summary {
  font-size: 13px;
  color: var(--text-muted);

  strong {
    font-weight: 600;
    color: var(--text-primary);
  }
}

.c-hreflang__alert {
  margin-bottom: 16px;
}

.c-hreflang__list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

// ---------- website card ----------
.c-hreflang-card {
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  background-color: var(--bg-surface);
  font-family: var(--font-sans);
  transition: border-color var(--duration-fast) ease, box-shadow var(--duration-fast) ease;

  &:not(.is-linked) {
    background-color: var(--bg-subtle);

    .c-hreflang-card__site {
      opacity: 0.7;
    }
  }

  &.is-linked {
    border-color: var(--border-strong);
    box-shadow: var(--shadow-xs);
  }

  &.is-locked {
    opacity: 0.5;
  }
}

.c-hreflang-card__main {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 16px 10px 12px;
}

.c-hreflang-card__order {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  width: 32px;
}

.c-hreflang-card__position {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
}

.c-hreflang-card__move {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 22px;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;

  &:hover:not(:disabled) {
    background-color: var(--bg-hover);
    color: var(--text-primary);
  }

  &:disabled {
    opacity: 0.3;
    cursor: default;
  }
}

.c-hreflang-card__switch {
  flex-shrink: 0;
  margin: 0;
}

.c-hreflang-card__site {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.c-hreflang-card__name {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.c-hreflang-card__url {
  overflow: hidden;
  font-size: 13px;
  color: var(--text-muted);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.c-hreflang-card__tag {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  height: 20px;
  padding: 0 8px;
  border-radius: var(--radius-full);
  background-color: var(--brand-50);
  color: var(--brand-fg);
  font-size: 11px;
  font-weight: 600;

  &--locked {
    background-color: var(--bg-hover);
    color: var(--text-secondary);
  }
}

.c-hreflang-card__country {
  flex-shrink: 0;
  min-width: 40px;
  padding: 3px 8px;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-sm);
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  color: var(--text-secondary);
}

.c-hreflang-card__xdefault {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  height: 30px;
  padding: 0 10px;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-full);
  background-color: var(--bg-surface);
  color: var(--text-secondary);
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color var(--duration-fast) ease, border-color var(--duration-fast) ease,
    color var(--duration-fast) ease;

  &:hover:not(:disabled) {
    border-color: var(--border-strong);
    color: var(--text-primary);
  }

  &.is-active {
    border-color: #f5c46b;
    background-color: #fff7e6;
    color: #b54708;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.meridian-theme-dark .c-hreflang-card__xdefault.is-active {
  border-color: #7a5410;
  background-color: #2b2110;
  color: #fdb022;
}

// ---------- included pages ----------
.c-hreflang-card__pages {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
  padding: 10px 16px 12px 108px;
  border-top: 1px dashed var(--border-default);
}

.c-hreflang-card__pages-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
}

.c-hreflang-card__warning {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
  color: var(--warning-fg);
}
</style>
